import {
  Accordion,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { LuAnchor, LuBuilding2, LuCheck, LuClock, LuEllipsis, LuFileText, LuKey, LuStar, LuUser } from 'react-icons/lu'
import { type CreateCustomerInput, CreateCustomerSchema } from '@/lib/marina/validators'

const CATEGORIES: {
  value: CreateCustomerInput['category']
  label: string
  hint: string
  icon: React.ReactNode
  color: string
}[] = [
  {
    value: 'TRANSIT',
    label: 'Transit',
    hint: 'Tamu sementara, kunjungan singkat',
    icon: <LuClock size={16} />,
    color: 'yellow',
  },
  {
    value: 'OWNER',
    label: 'Pemilik',
    hint: 'Pemilik berth permanen di marina',
    icon: <LuAnchor size={16} />,
    color: 'laut',
  },
  {
    value: 'RENTER',
    label: 'Penyewa',
    hint: 'Menyewa berth mingguan / bulanan',
    icon: <LuKey size={16} />,
    color: 'blue',
  },
  {
    value: 'MEMBER',
    label: 'Anggota',
    hint: 'Anggota klub bahari dengan privilege',
    icon: <LuStar size={16} />,
    color: 'violet',
  },
  {
    value: 'GUEST',
    label: 'Tamu',
    hint: 'Tamu dari pelanggan marina yang ada',
    icon: <LuUser size={16} />,
    color: 'cyan',
  },
  {
    value: 'OTHER',
    label: 'Lainnya',
    hint: 'Kasus khusus di luar kategori lain',
    icon: <LuEllipsis size={16} />,
    color: 'gray',
  },
]

interface Props {
  initialValues?: Partial<CreateCustomerInput>
  onSubmit: (values: CreateCustomerInput) => Promise<void> | void
  onCancel: () => void
  submitting?: boolean
  submitLabel?: string
}

export function CustomerForm({ initialValues, onSubmit, onCancel, submitting, submitLabel = 'Simpan' }: Props) {
  const form = useForm<CreateCustomerInput>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      type: 'INDIVIDUAL',
      category: 'TRANSIT',
      phone: '',
      mobile: '',
      address: '',
      city: '',
      country: '',
      postalCode: '',
      website: '',
      idType: '',
      idNumber: '',
      idExpiry: '',
      dateOfBirth: '',
      nationality: '',
      billingName: '',
      billingAddress: '',
      billingCity: '',
      billingCountry: '',
      billingPostalCode: '',
      taxId: '',
      companyName: '',
      companyRegNumber: '',
      notes: '',
      tags: [],
      ...initialValues,
    } as CreateCustomerInput,
    validate: zodResolver(CreateCustomerSchema),
  })

  const isCompany = form.values.type === 'COMPANY'

  // Count filled fields per section for indicators
  const contactFilled = [
    form.values.phone,
    form.values.mobile,
    form.values.address,
    form.values.city,
    form.values.country,
  ].filter(Boolean).length
  const idFilled = [form.values.idNumber, form.values.idExpiry, form.values.nationality].filter(Boolean).length
  const billingFilled = [form.values.taxId, form.values.billingName, form.values.billingAddress].filter(Boolean).length
  const notesFilled = !!form.values.notes

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="lg">
        {/* ── Section 1: Data Utama ─────────────────────── */}
        <Card radius="md" padding="lg">
          <Stack gap="md">
            <div>
              <Text fw={700} size="md" ff="var(--font-display)">
                Data Utama
              </Text>
              <Text c="dimmed" size="xs" mt={2}>
                Informasi wajib untuk mendaftarkan pelanggan.
              </Text>
            </div>

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput label="Nama Depan" placeholder="Budi" required {...form.getInputProps('firstName')} />
              <TextInput label="Nama Belakang" placeholder="Santoso" required {...form.getInputProps('lastName')} />
            </SimpleGrid>

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput
                label="Email"
                placeholder="budi@example.com"
                description="Opsional — dipakai untuk login portal"
                {...form.getInputProps('email')}
              />
              <Select
                label="Tipe Pelanggan"
                required
                data={[
                  { value: 'INDIVIDUAL', label: 'Perorangan' },
                  { value: 'COMPANY', label: 'Perusahaan' },
                ]}
                {...form.getInputProps('type')}
              />
            </SimpleGrid>

            {/* Company fields — only when type=COMPANY */}
            {isCompany && (
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Nama Perusahaan"
                  placeholder="PT. Bahari Nusantara"
                  leftSection={<LuBuilding2 size={14} />}
                  required
                  {...form.getInputProps('companyName')}
                />
                <TextInput
                  label="No. Registrasi Perusahaan"
                  placeholder="AHU-12345"
                  {...form.getInputProps('companyRegNumber')}
                />
              </SimpleGrid>
            )}

            {/* Category selector */}
            <div>
              <Text size="sm" fw={600} mb="xs">
                Kategori{' '}
                <Text component="span" c="red">
                  *
                </Text>
              </Text>
              <Grid>
                {CATEGORIES.map((cat) => {
                  const active = form.values.category === cat.value
                  return (
                    <Grid.Col key={cat.value} span={{ base: 6, sm: 4 }}>
                      <Card
                        radius="md"
                        padding="sm"
                        style={{
                          cursor: 'pointer',
                          outline: active ? `2px solid var(--mantine-color-laut-6)` : 'none',
                          outlineOffset: 2,
                          background: active
                            ? 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.12))'
                            : undefined,
                          transition: 'outline 120ms ease, background 120ms ease',
                        }}
                        onClick={() => form.setFieldValue('category', cat.value)}
                      >
                        <Group gap="xs" wrap="nowrap" mb={4}>
                          <ThemeIcon
                            variant={active ? 'filled' : 'light'}
                            color={active ? 'laut' : cat.color}
                            size="sm"
                            radius="xl"
                          >
                            {active ? <LuCheck size={11} /> : cat.icon}
                          </ThemeIcon>
                          <Text size="sm" fw={active ? 700 : 600} c={active ? 'laut.7' : undefined}>
                            {cat.label}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed" pl={28} lh={1.3}>
                          {cat.hint}
                        </Text>
                      </Card>
                    </Grid.Col>
                  )
                })}
              </Grid>
            </div>
          </Stack>
        </Card>

        {/* ── Accordion sections ────────────────────────── */}
        <Accordion multiple variant="contained" radius="md">
          {/* Kontak */}
          <Accordion.Item value="contact">
            <Accordion.Control>
              <Group gap="sm" align="center">
                <div style={{ flex: 1 }}>
                  <Text fw={600} size="sm">
                    Informasi Kontak
                  </Text>
                  <Text c="dimmed" size="xs">
                    Telepon, alamat, dan website
                  </Text>
                </div>
                {contactFilled > 0 && (
                  <Badge size="xs" color="teal" variant="light">
                    {contactFilled} diisi
                  </Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <SimpleGrid cols={{ base: 1, sm: 2 }} mt="xs">
                <TextInput label="Telepon" placeholder="+62 21 xxxx" {...form.getInputProps('phone')} />
                <TextInput label="HP / Mobile" placeholder="+62 812 xxxx" {...form.getInputProps('mobile')} />
                <TextInput label="Alamat" {...form.getInputProps('address')} />
                <TextInput label="Kota" {...form.getInputProps('city')} />
                <TextInput label="Negara" {...form.getInputProps('country')} />
                <TextInput label="Kode Pos" {...form.getInputProps('postalCode')} />
                <TextInput label="Website" placeholder="https://" {...form.getInputProps('website')} />
              </SimpleGrid>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Identifikasi */}
          <Accordion.Item value="id">
            <Accordion.Control>
              <Group gap="sm" align="center">
                <div style={{ flex: 1 }}>
                  <Group gap="xs" align="center">
                    <LuFileText size={14} />
                    <Text fw={600} size="sm">
                      Dokumen Identitas
                    </Text>
                  </Group>
                  <Text c="dimmed" size="xs">
                    KTP, paspor, dan informasi pribadi
                  </Text>
                </div>
                {idFilled > 0 && (
                  <Badge size="xs" color="teal" variant="light">
                    {idFilled} diisi
                  </Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <SimpleGrid cols={{ base: 1, sm: 2 }} mt="xs">
                <Select
                  label="Jenis Dokumen"
                  placeholder="Pilih jenis"
                  clearable
                  data={[
                    { value: 'passport', label: 'Paspor' },
                    { value: 'national_id', label: 'KTP / Kartu Identitas' },
                    { value: 'driver_license', label: 'SIM' },
                  ]}
                  {...form.getInputProps('idType')}
                />
                <TextInput label="Nomor Dokumen" {...form.getInputProps('idNumber')} />
                <TextInput label="Berlaku Sampai" type="date" {...form.getInputProps('idExpiry')} />
                <TextInput label="Tanggal Lahir" type="date" {...form.getInputProps('dateOfBirth')} />
                <TextInput label="Kewarganegaraan" placeholder="Indonesia" {...form.getInputProps('nationality')} />
              </SimpleGrid>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Penagihan */}
          <Accordion.Item value="billing">
            <Accordion.Control>
              <Group gap="sm" align="center">
                <div style={{ flex: 1 }}>
                  <Text fw={600} size="sm">
                    Informasi Penagihan
                  </Text>
                  <Text c="dimmed" size="xs">
                    NPWP, alamat tagihan, dan catatan
                  </Text>
                </div>
                {(billingFilled > 0 || notesFilled) && (
                  <Badge size="xs" color="teal" variant="light">
                    {billingFilled + (notesFilled ? 1 : 0)} diisi
                  </Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <SimpleGrid cols={{ base: 1, sm: 2 }} mt="xs">
                <TextInput label="Nama Tagihan" {...form.getInputProps('billingName')} />
                <TextInput label="NPWP" placeholder="00.000.000.0-000.000" {...form.getInputProps('taxId')} />
                {!isCompany && (
                  <>
                    <TextInput label="Nama Perusahaan" {...form.getInputProps('companyName')} />
                    <TextInput label="No. Registrasi Perusahaan" {...form.getInputProps('companyRegNumber')} />
                  </>
                )}
                <TextInput label="Alamat Tagihan" {...form.getInputProps('billingAddress')} />
                <TextInput label="Kota" {...form.getInputProps('billingCity')} />
                <TextInput label="Negara" {...form.getInputProps('billingCountry')} />
                <TextInput label="Kode Pos" {...form.getInputProps('billingPostalCode')} />
              </SimpleGrid>
              <Textarea
                label="Catatan"
                description="Informasi tambahan tentang pelanggan ini"
                rows={3}
                mt="md"
                {...form.getInputProps('notes')}
              />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        {/* ── Actions ───────────────────────────────────── */}
        <Group justify="flex-end" gap="sm" mt="xs">
          <Button variant="default" onClick={onCancel}>
            Batal
          </Button>
          <Button type="submit" color="laut" loading={submitting}>
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
